import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Link as RouterLink,
  useLocation,
  useNavigate,
} from "react-router-dom";

import type {
  CreatePersonRequest,
  PersonMatchRequest,
} from "@thalamos/haven-common";

import api from "../../api/index.js";
import { CreatePersonForm } from "../../components/CreatePersonForm/CreatePersonForm.js";
import type { PersonSaveOutcome } from "../../components/PersonForm/PersonForm.js";
import { config } from "../../config/index.js";
import { createRoute } from "../../routing/createRoute.js";
import { valuesFromHandOff } from "./createPerson.js";
import { readMatchHandOff } from "./matchPerson.js";

/** How a registration answered, as the form needs it. */
export const creationOutcome = (status: number): PersonSaveOutcome =>
  status === 201 ? "saved" : "failed";

/**
 * Registers a young person under the organisation the session acts for. The
 * record opens with nobody running the case; who does is named from the record
 * once it exists.
 */
export const NewPersonPage = () => {
  const navigate = useNavigate();
  const handOff = readMatchHandOff(useLocation().state);

  const save = async (
    request: CreatePersonRequest,
  ): Promise<PersonSaveOutcome> => {
    const response = await api.person.create(request);
    const outcome = creationOutcome(response.status);

    // The new record's page is where the person registering wants to be next,
    // and it is read fresh there rather than from what was sent.
    // The shape is checked before it is probed: a failure body is not
    // necessarily an object — a proxy or a gateway can answer with text — and
    // `in` throws on anything that is not one.
    if (
      outcome === "saved" &&
      typeof response.data === "object" &&
      response.data !== null &&
      "id" in response.data
    ) {
      navigate(createRoute.person(response.data.id));
    }

    return outcome;
  };

  // Registering is reached only from a search, so a duplicate is looked for first.
  if (handOff === null) {
    return <Navigate to={createRoute.matchPerson()} replace />;
  }

  return (
    <NewPersonPageContent
      search={handOff}
      onSave={save}
      placesApiKey={config.googlePlaces.apiKey}
    />
  );
};

/**
 * The page's rendering, with the save and the address search's key injected so
 * a story or a test can show it without an API client, Google, or a router
 * beyond the back link.
 */
export const NewPersonPageContent = ({
  search,
  onSave,
  placesApiKey,
}: {
  search: PersonMatchRequest;
  onSave: (request: CreatePersonRequest) => Promise<PersonSaveOutcome>;
  placesApiKey: string;
}) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Button
        component={RouterLink}
        to={createRoute.matchPerson()}
        // The search comes back as it was run, so going back loses nothing.
        state={search}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ alignSelf: "flex-start" }}
      >
        {t("personPage.creation.backToSearch")}
      </Button>

      <Stack spacing={1}>
        <Typography variant="h4" component="h1">
          {t("personPage.newTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("personPage.newDescription")}
        </Typography>
      </Stack>

      <CreatePersonForm
        initialValues={valuesFromHandOff(search)}
        onSave={onSave}
        placesApiKey={placesApiKey}
      />
    </Stack>
  );
};
